import {
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    TextField
} from "@mui/material";
import { ChangeEvent, KeyboardEvent, useEffect, useState } from "react";
import { theme } from "src/theme/mui-theme";
import { BaseDialog } from "src/components/BaseDialog/BaseDialog";
import { ts } from "src/utils/translate";
import { countries } from "src/utils/countries";
import {
    clearVatNumberIfNotApplicable,
    isVatCountry,
    upperCaseVatNumberInput,
    validateCompany,
    validateVatNumber,
    vatNumberExamples,
} from "src/utils/vat";
import { useGetBillingDataQuery, useSaveBillingDataMutation } from "src/api/v2/user-v2-ApiSlice";
import { GetBillingDataResponse } from "src/model/api/v2/data-contracts";
import { showSnackBar } from "src/api/ui-slice";
import { useAppDispatch } from "src/app/hooks";

interface BillingDataDialogProps {
    open: boolean;
    onClose: () => void;
    onSaved: () => void;
}

interface BillingFormData {
    country: string;
    city: string;
    zipcode: string;
    street_address: string;
    company: string;
    tax_number: string;
}

type BillingFormErrors = Partial<Record<keyof BillingFormData, string>>;

const EMPTY_FORM: BillingFormData = {
    country: "",
    city: "",
    zipcode: "",
    street_address: "",
    company: "",
    tax_number: "",
};

const toFormData = (billingData?: GetBillingDataResponse): BillingFormData => billingData ? {
    country: billingData.country,
    city: billingData.city,
    zipcode: billingData.zipcode,
    street_address: billingData.street_address,
    company: billingData.company,
    tax_number: billingData.tax_number,
} : EMPTY_FORM;

const validate = (form: BillingFormData): BillingFormErrors => {
    const required = ts("field_is_required", "This field is required.");
    const errors: BillingFormErrors = {};

    if (!form.country) errors.country = required;
    if (!form.city.trim()) errors.city = required;
    if (!form.zipcode.trim()) errors.zipcode = required;
    if (!form.street_address.trim()) errors.street_address = required;

    const companyError = validateCompany(form);
    if (companyError) errors.company = companyError;

    const taxNumberError = validateVatNumber(form);
    if (taxNumberError) errors.tax_number = taxNumberError;

    return errors;
};

const validationErrorsAfterChange = (
    field: keyof BillingFormData,
    nextForm: BillingFormData,
    errors: BillingFormErrors,
): BillingFormErrors => {
    const nextErrors: BillingFormErrors = { ...errors, [field]: undefined };

    if (field === "country" && (errors.tax_number || nextForm.tax_number.trim())) {
        nextErrors.tax_number = validateVatNumber(nextForm);
    }

    return nextErrors;
};

export const BillingDataDialog = ({ open, onClose, onSaved }: BillingDataDialogProps) => {

    const { data: billingData } = useGetBillingDataQuery(undefined, { skip: !open });
    const [saveBillingData, { isLoading: isSaveLoading }] = useSaveBillingDataMutation();

    const [form, setForm] = useState<BillingFormData>(EMPTY_FORM);
    const [formValidationErrors, setFormValidationErrors] = useState<BillingFormErrors>({});

    const isVatNumberVisible = isVatCountry(form.country);

    const dispatch = useAppDispatch();

    const showSaveError = () => dispatch(showSnackBar({
        text: ts("billing_data_save_failed", "Failed to save your billing data, please try again."),
        severity: 'error',
    }));

    useEffect(() => {
        if (!billingData) return;

        setForm(toFormData(billingData));
    }, [billingData]);

    const handleChange = (field: keyof BillingFormData) =>
        (event: SelectChangeEvent<string> | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            if (field === "tax_number" && event.target instanceof HTMLInputElement) {
                upperCaseVatNumberInput(event.target);
            }
            const nextForm = clearVatNumberIfNotApplicable({ ...form, [field]: event.target.value }, field);

            setForm(nextForm);
            setFormValidationErrors(validationErrorsAfterChange(field, nextForm, formValidationErrors));
        };

    const handleClose = () => {
        setForm(toFormData(billingData));
        setFormValidationErrors({});
        onClose();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Enter" && event.target instanceof HTMLInputElement) {
            handleConfirm();
        }
    };

    const handleConfirm = async () => {
        const validationErrors = validate(form);
        setFormValidationErrors(validationErrors);
        if (Object.keys(validationErrors).length) return;

        try {
            const response = await saveBillingData({
                country: form.country,
                city: form.city.trim(),
                zipcode: form.zipcode.trim(),
                street_address: form.street_address.trim(),
                company: form.company.trim(),
                tax_number: isVatNumberVisible ? form.tax_number.trim() : "",
            }).unwrap();

            if (!response.success) {
                showSaveError();
                return;
            }

            onSaved();
        } catch {
            showSaveError();
        }
    };

    return (
        <BaseDialog
            open={open}
            onClose={handleClose}
            title={ts("billing_data_required_title", "Billing data required")}
            subtitle={ts("billing_data_required_text", "In order to purchase pro license or other features, please fill out your country, city, zipcode, street address.")}
            titleColor={theme.palette.primary.main}
            maxWidth="xs"
            actionLabel={ts("plt_confirm", "Confirm")}
            onAction={handleConfirm}
            actionDisabled={isSaveLoading}
        >
            <Stack spacing={3} sx={{ mt: 1 }} onKeyDown={handleKeyDown}>
                <FormControl fullWidth size="small" required error={!!formValidationErrors.country}>
                    <InputLabel id="billing-country-label">{ts("country", "Country")}</InputLabel>
                    <Select
                        labelId="billing-country-label"
                        value={form.country}
                        onChange={handleChange("country")}
                        label={ts("country", "Country")}
                        MenuProps={{ PaperProps: { sx: { maxHeight: 250 } } }}
                    >
                        {countries.map(country => (
                            <MenuItem key={country} value={country}>{country}</MenuItem>
                        ))}
                    </Select>
                    {formValidationErrors.country && <FormHelperText>{formValidationErrors.country}</FormHelperText>}
                </FormControl>

                <TextField
                    label={ts("city", "City")}
                    value={form.city}
                    onChange={handleChange("city")}
                    error={!!formValidationErrors.city}
                    helperText={formValidationErrors.city}
                    required
                    fullWidth
                    size="small"
                />

                <TextField
                    label={ts("zip_code", "Zip code")}
                    value={form.zipcode}
                    onChange={handleChange("zipcode")}
                    error={!!formValidationErrors.zipcode}
                    helperText={formValidationErrors.zipcode}
                    required
                    fullWidth
                    size="small"
                />

                <TextField
                    label={ts("street_address", "Street address")}
                    value={form.street_address}
                    onChange={handleChange("street_address")}
                    error={!!formValidationErrors.street_address}
                    helperText={formValidationErrors.street_address}
                    required
                    fullWidth
                    size="small"
                />

                <TextField
                    label={ts("company", "Company")}
                    value={form.company}
                    onChange={handleChange("company")}
                    error={!!formValidationErrors.company}
                    helperText={formValidationErrors.company}
                    fullWidth
                    size="small"
                />

                {isVatNumberVisible && (
                    <TextField
                        label={ts("vat_number", "VAT number")}
                        value={form.tax_number}
                        onChange={handleChange("tax_number")}
                        error={!!formValidationErrors.tax_number}
                        helperText={formValidationErrors.tax_number ?? vatNumberExamples()}
                        fullWidth
                        size="small"
                    />
                )}
            </Stack>
        </BaseDialog>
    );
};
